// Change #100326 - Settings > WhatsApp > Quick replies. Typing "/" + the shortcut in the inbox
// composer inserts the message. A blank channel means the reply is offered on every channel.
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Button, Col, Input, Label, Row, Table } from "reactstrap";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import {
  createQuickReply,
  deleteQuickReply,
  errorText,
  listQuickReplies,
  ok,
  updateQuickReply,
} from "../../../../Components/WhatsApp/whatsappApi";
import { ActiveBadge, FieldHelp, ListState, Switch, Toolbar, channelName, isOn } from "./shared";

/** Same rule as the backend: lowercase letters, digits, "_" and "-". */
const cleanShortcut = (v) => String(v || "").replace(/[^A-Za-z0-9_-]+/g, "").toLowerCase();

const blank = () => ({ Name: "", Shortcut: "", Message: "", MessagingChannel_Id: "", Order: 0, FlagActive: 1 });

const QuickReplyModal = ({ open, row, channels, onClose, onSaved }) => {
  const [form, setForm] = useState(blank());
  const [saving, setSaving] = useState(false);
  const isEdit = !!row?.Id;

  useEffect(() => {
    if (!open) return;
    setForm(
      row
        ? {
            Name: row.Name || "",
            Shortcut: row.Shortcut || "",
            Message: row.Message || "",
            MessagingChannel_Id: row.MessagingChannel_Id ? String(row.MessagingChannel_Id) : "",
            Order: row.Order ?? 0,
            FlagActive: row.FlagActive === null || row.FlagActive === undefined || isOn(row.FlagActive) ? 1 : 0,
          }
        : blank()
    );
  }, [open, row]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSave = form.Name.trim() && form.Message.trim() && !saving;

  const save = async () => {
    if (!canSave) return;
    const payload = {
      Name: form.Name.trim(),
      Shortcut: cleanShortcut(form.Shortcut) || null,
      Message: form.Message,
      MessagingChannel_Id: form.MessagingChannel_Id || null,
      Order: Number(form.Order) || 0,
      FlagActive: form.FlagActive,
    };
    if (isEdit) payload.Id = row.Id;
    setSaving(true);
    const res = await (isEdit ? updateQuickReply(payload) : createQuickReply(payload));
    setSaving(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not save the quick reply"));
      return;
    }
    toast.success(res.message || "Saved");
    onSaved();
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={isEdit ? "Edit quick reply" : "New quick reply"}
      subtitle="Canned text staff can drop into a WhatsApp reply"
      icon="ri-flashlight-line"
      headerAccent="emerald"
      size="md"
      loading={saving}
      onSubmit={save}
      submitDisabled={!canSave}
    >
      <Row className="g-3">
        <Col md={7}>
          <Label htmlFor="wa-qr-name">Name</Label>
          <Input id="wa-qr-name" value={form.Name} onChange={(e) => set("Name", e.target.value)} placeholder="e.g. Opening hours" />
        </Col>
        <Col md={5}>
          <Label htmlFor="wa-qr-short">Shortcut</Label>
          <div className="input-group">
            <span className="input-group-text">/</span>
            <Input
              id="wa-qr-short"
              value={form.Shortcut}
              onChange={(e) => set("Shortcut", cleanShortcut(e.target.value))}
              placeholder="hours"
            />
          </div>
          <FieldHelp>Type /{form.Shortcut || "shortcut"} in the inbox to insert it.</FieldHelp>
        </Col>
        <Col xs={12}>
          <Label htmlFor="wa-qr-msg">Message</Label>
          <Input id="wa-qr-msg" type="textarea" rows={5} value={form.Message} onChange={(e) => set("Message", e.target.value)} />
        </Col>
        <Col md={7}>
          <Label htmlFor="wa-qr-ch">Channel</Label>
          <Input id="wa-qr-ch" type="select" value={form.MessagingChannel_Id} onChange={(e) => set("MessagingChannel_Id", e.target.value)}>
            <option value="">All channels</option>
            {channels.map((c) => (
              <option key={c.Id} value={String(c.Id)}>
                {channelName(c)}
              </option>
            ))}
          </Input>
        </Col>
        <Col md={5}>
          <Label htmlFor="wa-qr-order">Order</Label>
          <Input id="wa-qr-order" type="number" min={0} value={form.Order} onChange={(e) => set("Order", e.target.value)} />
          <FieldHelp>Lower numbers are listed first.</FieldHelp>
        </Col>
        <Col xs={12}>
          <Switch id="wa-qr-active" checked={isOn(form.FlagActive)} onChange={(v) => set("FlagActive", v)} label="Active" />
        </Col>
      </Row>
    </AppModal>
  );
};

QuickReplyModal.propTypes = {
  open: PropTypes.bool,
  row: PropTypes.object,
  channels: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

const QuickRepliesTab = ({ channels }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const res = await listQuickReplies();
    setLoading(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not load quick replies"));
      return;
    }
    setRows(res.messagingconversationquickreply_list || []);
  };

  useEffect(() => {
    load();
  }, []);

  const confirmDelete = async () => {
    setDeleteBusy(true);
    const res = await deleteQuickReply(deleting.Id);
    setDeleteBusy(false);
    if (!ok(res)) {
      toast.error(errorText(res, "Could not delete the quick reply"));
      return;
    }
    toast.success(res.message || "Deleted");
    setDeleting(null);
    load();
  };

  const channelLabel = (id) => {
    if (!id) return <span className="text-muted">All channels</span>;
    const c = channels.find((x) => String(x.Id) === String(id));
    return c ? channelName(c) : `Channel #${id}`;
  };

  const newButton = (
    <Button color="primary" size="sm" onClick={() => setEditing({})}>
      <i className="ri-add-line me-1" />
      New quick reply
    </Button>
  );

  return (
    <>
      <Toolbar note='Staff type "/" in the WhatsApp composer to pick one.'>{newButton}</Toolbar>

      <ListState
        loading={loading && !rows.length}
        empty={!rows.length}
        icon="ri-flashlight-line"
        title="No quick replies yet"
        text="Save the answers you give most often - opening hours, directions, how to pay."
        action={newButton}
      >
        <div className="table-responsive">
          <Table className="table-sm align-middle mb-0">
            <thead className="text-muted">
              <tr>
                <th>Name</th>
                <th>Shortcut</th>
                <th>Message</th>
                <th>Channel</th>
                <th className="text-end">Order</th>
                <th>Status</th>
                <th className="text-end">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((q) => (
                <tr key={q.Id}>
                  <td className="fw-medium">{q.Name}</td>
                  <td>{q.Shortcut ? <code>/{q.Shortcut}</code> : <span className="text-muted">-</span>}</td>
                  <td>
                    <div className="wa-settings__truncate" title={q.Message}>
                      {q.Message}
                    </div>
                  </td>
                  <td>{channelLabel(q.MessagingChannel_Id)}</td>
                  <td className="text-end">{q.Order ?? 0}</td>
                  <td>
                    <ActiveBadge active={q.FlagActive === null || q.FlagActive === undefined ? 1 : q.FlagActive} />
                  </td>
                  <td className="text-end text-nowrap">
                    <Button color="light" size="sm" className="me-1" title="Edit" onClick={() => setEditing(q)}>
                      <i className="ri-pencil-fill align-bottom" />
                    </Button>
                    <Button color="light" size="sm" title="Delete" onClick={() => setDeleting(q)}>
                      <i className="ri-delete-bin-line align-bottom text-danger" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </ListState>

      <QuickReplyModal
        open={!!editing}
        row={editing && editing.Id ? editing : null}
        channels={channels}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />

      <DeleteModal
        show={!!deleting}
        loading={deleteBusy}
        onCloseClick={() => setDeleting(null)}
        onDeleteClick={confirmDelete}
        customText={`Delete the quick reply "${deleting?.Name || ""}"?`}
      />
    </>
  );
};

QuickRepliesTab.propTypes = { channels: PropTypes.array.isRequired };

export default QuickRepliesTab;
